import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Body from "src/components/common/Body";
import Box from "src/components/common/Box";
import Button from "src/components/common/Button";
import SearchInput from "src/components/common/SearchInput";
import List from "src/components/common/List";
import SearchSortItem from "src/components/filter/SearchSortItem";
import BottomModal, { RefProps } from "src/components/common/BottomModal";
import SVG from "src/utils/SVG";
import styled from "styled-components";
import FulfillListItem from "src/components/notice/FulfillListItem";
import ComfirmItem from "src/components/notice/ComfirmItem";
import { useSelectGroupQuery, useSelectStoreQuery } from "../../api/userAPI";
import reuseQueryStringV2 from "../../utils/reuseQueryStringV2";
import SearchStoreFilterItemV2 from "../../components/filter/SearchStoreFilterItemV2";
import Chip from "../../components/common/Chip";
import GroupItemV2 from "../../components/manage/GroupItemV2";
import { useAppSelector } from "../../store/actions/action";
import { useDispatch } from "react-redux";
import { setNoticeRegister } from "../../store/reducers/noticeReduce";
import { useExcelTemplateMutation, useGetGroupStoreQuery } from "../../api/noticeAPI";
import BottomModalV2 from "../../components/common/BottomModalV2";
import PreviousNoticeListModal from "../../components/notice/PreviousNoticeListModal";
import PreviousExecuteStoresModal from "../../components/notice/PreviousExecuteStoresModal";
import ExcelStoreUploadModal from "../../components/notice/ExcelStoreUploadModal";
import mergeStoreIds from "../../utils/mergeStoreIds";

const initStore: StoreFilter[] = [
  { storeType: "백화점", checked: false },
  { storeType: "대리점", checked: false },
  { storeType: "직영점", checked: false },
  { storeType: "면세점", checked: false },
];
const initFilter: Filter = { sortSearchString: "최신순", newStoreSearchString: "", typeSearchArray: [], contentsSearchString: "" };

function fulfillSelectList() {
  const navigate = useNavigate();
  const [content, setContent] = useState("");
  const [sortFilter, setSortFilter] = useState<Filter>(initFilter);
  const [strSortFilter, setStrSortFilter] = useState(reuseQueryStringV2(sortFilter));
  const storeListAPI = useSelectStoreQuery(strSortFilter);
  // 이행대상 확인 모달에서 사용할 전체 매장 리스트 (필터 없음, initFilter 재사용)
  const allStoreListAPI = useSelectStoreQuery(reuseQueryStringV2(initFilter));
  const noticeData = useAppSelector((state) => state.noticeReduce);
  const [storeList, setStoreList] = useState<Store[]>([]);
  const [newStoreToggle, setNewStoreToggle] = useState<boolean>(false);
  const filterModalRef = React.useRef<RefProps>(null);
  const [storeFilterList, setStoreFilterList] = useState<StoreFilter[]>(initStore);
  const [applyStoreFilterList, setApplyStoreFilterList] = useState<StoreFilter[]>(initStore);
  //useInfo -> Group 목록 가져오기
  const userInfo = useAppSelector((state) => state.userReduce.userInfo);
  const groupAPI = useSelectGroupQuery({ userId: userInfo.user_id, type: "S" });
  const [selectedGroup, setSelectedGroup] = useState<Group>({ group_part_idx: 1, group_name: "", group_purpose: "" });
  //Group 검색 기능
  const [groupContent, setGroupContent] = useState("");
  const [sortGroupFilter, setSortGroupFilter] = useState<Filter>(initFilter);
  const [strGroupSortFilter, setStrGroupSortFilter] = useState(reuseQueryStringV2(sortGroupFilter));
  const groupStoreAPI = useGetGroupStoreQuery({
    groupId: selectedGroup.group_part_idx,
    queryString: strGroupSortFilter,
  });
  const dispatch = useDispatch();
  const filterListRef = useRef<HTMLDivElement>(null);

  // 유효한 매장 리스트를 useMemo로 캐싱하여 불필요한 재계산 방지
  const validStores = useMemo(() => {
    const allStores = allStoreListAPI?.data?.data || [];
    return noticeData.beforeStores?.filter(storeId =>
      allStores.some((store: Store) => store.user_id === storeId)
    ) || [];
  }, [allStoreListAPI?.data?.data, noticeData.beforeStores]);

  useEffect(() => {
    dispatch(setNoticeRegister({ beforeStores: noticeData.stores }));
  }, []);

  useEffect(() => {
    filterListRef.current && filterListRef.current.scrollTo({ behavior: "smooth", left: filterListRef.current.scrollLeft + filterListRef.current.scrollWidth });
  }, [applyStoreFilterList]);

  const openFilterModal = useCallback(() => {
    if (filterModalRef.current) filterModalRef.current.modalOpen();
  }, []);

  useEffect(() => {
    setStrSortFilter(reuseQueryStringV2(sortFilter));
  }, [sortFilter]);
  useEffect(() => {
    setStrGroupSortFilter(reuseQueryStringV2(sortGroupFilter));
  }, [sortGroupFilter]);
  useEffect(() => {
    storeListAPI?.data?.data && setStoreList(storeListAPI.data.data);
  }, [storeListAPI]);

  useEffect(() => {
    const tempTypeSearchArray: string[] = [];
    applyStoreFilterList.forEach((value) => value.checked && tempTypeSearchArray.push(value.storeType));
    setSortFilter({ ...sortFilter, typeSearchArray: tempTypeSearchArray });
  }, [applyStoreFilterList]);
  // 정렬모달 관련
  const [sortIdx, setSordIdx] = useState<number>(0);
  const sortModalRef = React.useRef<RefProps>(null);

  const openSortModal = useCallback(() => {
    if (sortModalRef.current) sortModalRef.current.modalOpen();
  }, []);
  //React.MouseEventHandler<HTMLButtonElement>
  const sortBtn = (e: any) => {
    const sortName = e.target.textContent;
    sortName && setSortFilter({ ...sortFilter, sortSearchString: sortName });
    if (sortModalRef.current) sortModalRef.current.modalClose();
  };

  const childrenSortIdxFunc = (idx: number) => {
    setSordIdx(idx);
  };

  // 그룹모달 관련
  const groupModalRef = React.useRef<RefProps>(null);
  const confirmModalRef = React.useRef<RefProps>(null);
  const previousNoticeModalRef = React.useRef<RefProps>(null);
  const previousExecuteStoresModalRef = React.useRef<RefProps>(null);
  const [selectedNotice, setSelectedNotice] = useState<any>(null);
  const openGroupModal = useCallback(() => {
    setGroupContent("");
    setSortGroupFilter({ ...sortGroupFilter, contentsSearchString: "" });
    if (groupModalRef.current) {
      groupModalRef.current.modalOpen();
    }
  }, []);

  //그룹 채널 적용
  function onConfirm() {
    const storeData = mergeStoreIds(noticeData.beforeStores ?? [], noticeData.tempStores ?? [], noticeData.deleteTempStore ?? []);

    dispatch(setNoticeRegister({ beforeStores: storeData }));
    dispatch(setNoticeRegister({ tempStores: [] }));
    dispatch(setNoticeRegister({ deleteTempStore: [] }));
  }

  const openConfirmModal = useCallback(() => {
    if (confirmModalRef.current) {
      confirmModalRef.current.modalOpen();
    }
  }, []);

  function closeConfirmModal() {
    // useMemo로 캐싱된 validStores 사용
    dispatch(setNoticeRegister({ stores: validStores }));
    if (confirmModalRef.current) confirmModalRef.current.modalClose();
    navigate(-1);
  }

  const newStoreBtn = () => {
    if (newStoreToggle) {
      setSortFilter({ ...sortFilter, newStoreSearchString: "" });
    } else {
      setSortFilter({ ...sortFilter, newStoreSearchString: "T" });
    }
    setNewStoreToggle(!newStoreToggle);
  };
  const [initVisible, setInitVisible] = useState<boolean>(false);
  const setInitFunc = (propsSelectedFliterCheck: boolean) => {
    setInitVisible(propsSelectedFliterCheck);
  };
  const closeBtn = () => {
    setStoreFilterList(applyStoreFilterList);
  };
  const confirmBtn = () => {
    setApplyStoreFilterList(storeFilterList);
    filterModalRef.current && filterModalRef.current.modalClose();
  };
  const resetButton = () => {
    setStoreFilterList(initStore);
  };

  function changeChecked(value: StoreFilter) {
    const temp: StoreFilter[] = storeFilterList.map((filter) =>
      filter.storeType === value.storeType ? { ...filter, checked: !filter.checked } : { ...filter, checked: filter.checked },
    );
    setStoreFilterList(temp);
  }

  function deleteFilter(value: StoreFilter) {
    const temp: StoreFilter[] = applyStoreFilterList.map((filter) => (filter.storeType === value.storeType ? { ...filter, checked: false } : filter));
    setApplyStoreFilterList(temp);
    setStoreFilterList(temp);
  }

  function searchContents(e: any) {
    const temp = e?.currentTarget.parentElement?.childNodes[0].value;
    setContent(temp);
    setSortFilter({ ...sortFilter, contentsSearchString: temp });
  }

  function searchGroupContents(e: any) {
    const temp = e?.currentTarget.parentElement?.childNodes[0].value;
    setGroupContent(temp);
    setSortGroupFilter({ ...sortGroupFilter, contentsSearchString: temp });
  }

  function onKeyPress(e: any) {
    e.key === "Enter" && searchContents(e);
  }

  function onKeyGorupPress(e: any) {
    e.key === "Enter" && searchGroupContents(e);
  }

  function deleteSearchContents() {
    setContent("");
    setSortFilter({ ...sortFilter, contentsSearchString: "" });
  }

  function deleteSearchGroupContents() {
    setGroupContent("");
    setSortGroupFilter({ ...sortFilter, contentsSearchString: "" });
  }
  // 이전 공지 이행매장 불러오기 관련
  const openPreviousNoticeModal = useCallback(() => {
    if (previousNoticeModalRef.current) {
      previousNoticeModalRef.current.modalOpen();
    }
  }, []);

  const handleSelectNotice = useCallback((notice: any) => {
    setSelectedNotice(notice);
    if (previousExecuteStoresModalRef.current) {
      previousExecuteStoresModalRef.current.modalOpen();
    }
  }, []);

  const handleApplyExecuteStores = useCallback(() => {
    // Redux를 통해 이미 beforeStores에 병합되어 있음
    // 추가 작업이 필요하면 여기에 구현
  }, []);

  // 엑셀 업로드로 이행매장 선택 관련 (VMD-392)
  const excelUploadModalRef = React.useRef<RefProps>(null);
  const [excelTemplate, { isLoading: isTemplateDownloading }] = useExcelTemplateMutation();

  const openExcelUploadModal = useCallback(() => {
    if (excelUploadModalRef.current) {
      excelUploadModalRef.current.modalOpen();
    }
  }, []);

  // 템플릿 다운로드 - 파일명은 API 레이어에서 Content-Disposition 기준으로 저장
  const downloadExcelTemplate = async () => {
    if (isTemplateDownloading) return;
    try {
      await excelTemplate().unwrap();
    } catch (error) {
      console.error("템플릿 다운로드 실패:", error);
      alert("템플릿 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
  };

  // 엑셀 매칭 결과를 기존 선택에 누적 반영 (중복 제거는 mergeStoreIds 가 담당, 서버 저장은 기존 등록·수정 플로우 그대로)
  function handleApplyExcelStores(matchedStoreIds: string[]) {
    const storeData = mergeStoreIds(noticeData.beforeStores ?? [], matchedStoreIds, []);
    dispatch(setNoticeRegister({ beforeStores: storeData }));
  }

  //그룹 채널 선택
  function onClick(target: Group) {
    dispatch(setNoticeRegister({ tempStores: noticeData.beforeStores }));
    openGroupModal();
    setSelectedGroup(target);
  }
  //취소 및 뒤로가기
  function back() {
    if (noticeData.beforeStores && noticeData.stores && noticeData.beforeStores.toString() !== noticeData.stores.toString()) {
      if (window.confirm("선택한 이행매장을 저장하지 않고 나가시겠습니까?")) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  }

  function modalCloseAlert() {
    return window.confirm("선택한 이행매장을 저장하지 않고 나가시겠습니까?");
  }

  function groupChannalClose() {
    let alert = false;
    if (noticeData.tempStores && noticeData.beforeStores) {
      try {
        if (JSON.stringify(noticeData.tempStores) !== JSON.stringify(noticeData.beforeStores)) alert = true;
      } catch (e) {
        console.error(e);
      }
    }
    if (noticeData.deleteTempStore && noticeData.deleteTempStore.length > 0) alert = true;

    if (alert === false) return true;

    if (alert && modalCloseAlert()) {
      dispatch(setNoticeRegister({ tempStores: [] }));
      dispatch(setNoticeRegister({ deleteTempStore: [] }));
      return true;
    } else {
      return false;
    }
  }

  return (
    <>
      <header>
        <div>
          <Button variant="text" className="btn-back" onClick={() => back()}>
            이행매장
          </Button>
        </div>
        <div className="btn-box">
          <Button variant="text" onClick={openConfirmModal}>
            저장
          </Button>
        </div>
      </header>

      <Body type="detail">
        <Box pd="20px 15px">
          <SearchInput
            type="text"
            id="search-input"
            placeholder="검색어를 입력해주세요."
            defaultValue={content}
            onClick={(e) => searchContents(e)}
            onKeyPress={(e) => onKeyPress(e)}
            deleteSearchContentFunc={deleteSearchContents}
          />
        </Box>

        <GroupItemV2 group={groupAPI?.data as Group[]} onClick={onClick} />

        <BulkSelectButtonContainer>
          <Button variant="contained" br="round" color="primary" size="medium" onClick={openPreviousNoticeModal}>
            이전 공지 이행매장 불러오기
          </Button>
          <div className="excel-row">
            <Button variant="outlined" br="round" color="secondary" size="medium" onClick={downloadExcelTemplate}>
              <SVG name="fileDownload" width={16} height={16} viewBox="0 0 20 20" />
              {isTemplateDownloading ? "다운로드 중..." : "템플릿 다운로드"}
            </Button>
            <Button variant="contained" br="round" color="primary" size="medium" onClick={openExcelUploadModal}>
              엑셀 업로드
            </Button>
          </div>
        </BulkSelectButtonContainer>

        <FilterBtnSet ref={filterListRef}>
          <Button variant="contained" br="round" color="secondary" size="medium" onClick={openFilterModal}>
            <SVG name="filter" width={16} height={16} viewBox="0 0 16 16" />
            필터
          </Button>
          <Button variant="contained" br="round" color="secondary" size="medium" onClick={openSortModal}>
            <SVG name="sort" width={14.637} height={16.348} viewBox="0 0 14.637 16.348" />
            {sortFilter.sortSearchString}
          </Button>
          <Button className="only-new" variant="contained" br="round" color={newStoreToggle ? "active" : "secondary"} size="medium" onClick={newStoreBtn}>
            {newStoreToggle ? (
              <SVG name="toggleActive" width={16} height={16} viewBox="0 0 16 16" />
            ) : (
              <SVG name="toggle" width={16} height={16} viewBox="0 0 16 16" />
            )}
            신규매장만
          </Button>
          {applyStoreFilterList.map((value, index) => {
            return (
              value.checked && (
                <Chip variant="contained" deleted={true} key={index} onDelete={() => deleteFilter(value)}>
                  {value.storeType}
                </Chip>
              )
            );
          })}
        </FilterBtnSet>

        <FulfillListItem storeList={storeList} reduxName={"beforeStores"} />
      </Body>

      <BottomModal
        ref={filterModalRef}
        onClick={() => resetButton()}
        onClick2={() => confirmBtn()}
        onClick3={() => closeBtn()}
        modalTitle="검색 필터"
        headerBtn="초기화"
        footerBtn="적용"
        dimClickFun={closeBtn}
        initVisible={initVisible}
      >
        <List pd="0 15px" itemPd="20px 0">
          <SearchStoreFilterItemV2 title={"매장 유형"} filterList={storeFilterList} changeChecked={changeChecked} />
        </List>
      </BottomModal>

      <BottomModal modalTitle="정렬" ref={sortModalRef}>
        <List pd="0 15px">
          <SearchSortItem sortData={["최신순", "오래된순", "가나다순"]} onClick={(e) => sortBtn(e)} initIdx={sortIdx} parentSortIdxFunc={childrenSortIdxFunc} />
        </List>
      </BottomModal>

      <BottomModalV2 modalTitle={selectedGroup.group_name} footerBtn="적용" onConfirm={onConfirm} onClose={groupChannalClose} ref={groupModalRef}>
        <Box pd="20px 15px">
          <SearchInput
            type="text"
            id="search-input"
            placeholder="검색어를 입력해주세요."
            defaultValue={groupContent}
            onClick={(e) => searchGroupContents(e)}
            onKeyPress={(e) => onKeyGorupPress(e)}
            deleteSearchContentFunc={deleteSearchGroupContents}
          />
        </Box>
        <Box pd="0 15px">
          <Box pd="0">{groupStoreAPI?.data?.data && <FulfillListItem storeList={groupStoreAPI.data.data} reduxName={"tempStores"} />}</Box>
        </Box>
      </BottomModalV2>

      <BottomModal modalTitle="이행대상 확인" footerBtn="확인" onClick2={closeConfirmModal} ref={confirmModalRef}>
        <Box pd="0 15px">
          <ComfirmMes>
            선택한 대상은 총 <span>{validStores.length}</span>개입니다.
            <br />
            실수가 없는지 다시 한번 확인해주세요!
          </ComfirmMes>
        </Box>
        <List pd="0 15px" itemPd="14px 0">
          <ComfirmItem />
        </List>
      </BottomModal>

      <PreviousNoticeListModal
        ref={previousNoticeModalRef}
        onSelectNotice={handleSelectNotice}
      />

      <PreviousExecuteStoresModal
        ref={previousExecuteStoresModalRef}
        selectedNotice={selectedNotice}
        onApply={handleApplyExecuteStores}
      />

      <ExcelStoreUploadModal ref={excelUploadModalRef} onApply={handleApplyExcelStores} />
    </>
  );
}

const FilterBtnSet = styled.div`
  overflow: hidden;
  overflow-x: scroll;
  width: 100%;
  padding: 15px;
  border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
  ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start")}
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
  > * {
    flex: 0 0 auto;
    margin-right: 10px;
    &:last-child {
      margin-right: 0;
    }
  }
  > button {
    > svg {
      position: relative;
      top: -1px;
    }
  }
  .only-new {
    > svg {
      top: 2px;
      left: 2px;
    }
  }
`;

// 대량 선택 진입점 (이전 공지 이행매장 불러오기 / 템플릿 다운로드 / 엑셀 업로드)
const BulkSelectButtonContainer = styled.div`
  padding: 15px;
  border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};

  > button {
    width: 100%;
    ${({ theme }) => theme.flexBox.flexMixin()};
  }
  .excel-row {
    ${({ theme }) => theme.flexBox.flexMixin("row", "space-between")}
    margin-top: 10px;
    > button {
      flex: 1 1 0;
      ${({ theme }) => theme.flexBox.flexMixin("row", "center")};
      height: 28px;
      line-height: 26px;
      font-size: 14px;
      &:first-child {
        margin-right: 10px;
      }
      > svg {
        margin-right: 5px;
      }
    }
  }
`;

const ComfirmMes = styled.p`
  padding: 16px 0 24px;
  margin-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
  text-align: center;
  line-height: 20px;
  > span {
    font-family: "Poppins";
    font-size: 24px;
    color: ${({ theme }) => theme.color.main};
  }
`;

export default fulfillSelectList;
